<script setup>
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useIntervalFn } from '@vueuse/core'
import { useIntelichekFetch } from '@/lib/intelichek-fetch.js'

import QuotePrice from '@/views/my_garage/components/quote_price.vue'
import QuotePriceGhost from '@/views/my_garage/components/quote_price_ghost.vue'
import { formatDate } from '@/lib/date_helper.js'
import DropdownMenu from '@/components/ui/dropdown-menu.vue'

const props = defineProps({
  quote: {
    type: Object,
    required: true
  }
})

// I18n
const { t, tm } = useI18n()

// Variables
const isLoading = ref(true)
const quotesSortDirection = ref(t('my_garage.quotes.sort_price_asc'))

// Query User Quote Prices
const userQuotePricesUrl = computed(() => {
  if (!props.quote?.id) return

  const url = new URL(`${import.meta.env.VITE_BACKEND_URL}/api/v2/servicechek/user_quote_prices/quote_prices_for_quote`)
  url.searchParams.append('quote_id', props.quote.id)

  return url.href
})
const {
  isFetching: isUserQuotePricesLoading,
  data: userQuotePrices,
  execute: getUserQuotePrices
} = useIntelichekFetch(userQuotePricesUrl, { refetch: true, immediate: true }).json()

const { pause, resume, isActive } = useIntervalFn(async () => {
  getUserQuotePrices()
}, 5000)

function sortByQuotePrices(a, b) {
  switch (quotesSortDirection.value) {
    case t('my_garage.quotes.sort_price_desc'):
      return b.price.price - a.price.price
    case t('my_garage.quotes.sort_price_asc'):
      return a.price.price - b.price.price
    case t('my_garage.quotes.sort_distance_nearest'):
      return a.distance_to_provider - b.distance_to_provider
  }
}
</script>

<template>
  <div class="flex flex-col rounded-xl border bg-gray-100 w-full p-4 md:p-8 mb-8" v-if="quote">
    <div class="flex justify-between items-baseline">
      <div class="flex flex-col md:flex-row gap-2">
        <p class="uppercase">{{ t('my_garage.quotes.requested', { date: formatDate(quote.created_at) }) }}</p>
        <p class="hidden md:block">-</p>
        <p v-if="quote.user_vehicle_service_histories && quote.user_vehicle_service_histories[0] && quote.user_vehicle_service_histories[0].status === 'cancelled'" class="uppercase">{{ t('my_garage.quote_card.cancelled') }}</p>
        <p v-else class="uppercase">{{ t('my_garage.quotes.expires', { date: formatDate(quote.expires_at) }) }}</p>
      </div>

      <p class="mr-4">
        <dropdown-menu dropdown-width="700px">
          <template #link>
            <span class="text-gray-700 underline decoration-dotted">{{ t('request_a_quote.quote_disclaimer_dropdown') }}</span>
          </template>
          <template #dropdown>
            <li v-for="blurb in tm('request_a_quote.quote_disclaimer')" class="text-sm p-2">
              {{ blurb }}
            </li>
          </template>
        </dropdown-menu>
      </p>
    </div>


    <div class="flex justify-between items-baseline">
      <h4 class="text-2xl font-semibold my-4">{{ quote.service_title }}</h4>
      <p class="m-4">
        <dropdown-menu>
          <template #link>
            <span>{{ t('my_garage.quotes.sort', { direction: quotesSortDirection }) }}</span>
          </template>
          <template #dropdown>
            <li class="block px-4 py-2 text-sm font-semibold text-gray-700 cursor-pointer" role="menuitem"
                @click="quotesSortDirection = t('my_garage.quotes.sort_price_asc')">
              {{ t('my_garage.quotes.sort', { direction: t('my_garage.quotes.sort_price_asc') }) }}
            </li>
            <li class="block px-4 py-2 text-sm font-semibold text-gray-700 cursor-pointer" role="menuitem"
                @click="quotesSortDirection = t('my_garage.quotes.sort_price_desc')">
              {{ t('my_garage.quotes.sort', { direction: t('my_garage.quotes.sort_price_desc') }) }}
            </li>
            <li class="block px-4 py-2 text-sm font-semibold text-gray-700 cursor-pointer" role="menuitem"
                @click="quotesSortDirection = t('my_garage.quotes.sort_distance_nearest')">
              {{ t('my_garage.quotes.sort', { direction: t('my_garage.quotes.sort_distance_nearest') }) }}
            </li>
          </template>
        </dropdown-menu>
      </p>
    </div>

    <div class="flex justify-between items-baseline">
      <h5>{{ quote.user_vehicle.full_vehicle_name }}</h5>
    </div>

    <div class="pt-8" v-if="userQuotePrices?.data">
      <QuotePrice v-for="userQuotePrice in userQuotePrices.data.sort(sortByQuotePrices)" :key="userQuotePrice.id"
                  :quote="quote" :all-quote-prices="userQuotePrices.data" :user-quote-price="userQuotePrice"></QuotePrice>

      <div v-if="userQuotePrices.data.length < 4">
        <div v-for="index in 4-userQuotePrices.data.length" :key="index">
          <QuotePriceGhost :is-cancelled="quote.user_vehicle_service_histories?.[0]?.status === 'cancelled'" :is-expired="quote.job_status === 'expired'" :is-stale="quote.job_status === 'stale'"></QuotePriceGhost>
        </div>
      </div>
    </div>
    <div v-else class="pt-8">
      <div v-for="index in 4" :key="index">
        <QuotePriceGhost :is-cancelled="quote.user_vehicle_service_histories?.[0]?.status === 'cancelled'" :is-expired="quote.job_status === 'expired'" :is-stale="quote.job_status === 'stale'"></QuotePriceGhost>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@/assets/styles/views/my_garage/quote';
</style>
